import { useEffect, useRef, useState } from "react";
import { Camera, Upload } from "lucide-react";

import { Modal } from "@/components/modal";
import { Button } from "@/components/ui";

/** Live selfie via getUserMedia (front camera) with a file-picker fallback when the camera is blocked/unavailable. */
export function SelfieCamera({ open, onClose, onCapture }) {
  const video = useRef(null);
  const stream = useRef(null);
  const fileInput = useRef(null);
  const [state, setState] = useState("explain"); // explain | live | blocked

  const stop = () => { stream.current?.getTracks().forEach((t) => t.stop()); stream.current = null; };
  useEffect(() => {
    if (!open) { stop(); setState("explain"); return undefined; }
    navigator.permissions?.query({ name: "camera" }).then((p) => { if (p.state === "denied") setState("blocked"); }).catch(() => {});
    return stop;
  }, [open]);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) return setState("blocked");
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      setState("live");
      setTimeout(() => { if (video.current) { video.current.srcObject = stream.current; video.current.play().catch(() => {}); } }, 0);
    } catch {
      setState("blocked");
    }
  };

  const capture = () => {
    const v = video.current;
    if (!v?.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((b) => { if (b) { stop(); onCapture(new File([b], "selfie.jpg", { type: "image/jpeg" })); } }, "image/jpeg", 0.9);
  };

  const onFile = (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) { stop(); onCapture(f); } };

  return (
    <Modal open={open} onClose={onClose} title={state === "blocked" ? "Camera access is off" : state === "live" ? "Take your selfie" : "Allow camera access"} testId="camera-permission-sheet">
      <input ref={fileInput} type="file" accept="image/*" capture="user" className="hidden" data-testid="selfie-file-input" onChange={onFile} />
      {state === "live" ? (
        <>
          <div className="overflow-hidden rounded-[20px] bg-[#2A2727]"><video ref={video} data-testid="selfie-video" playsInline muted className="aspect-[3/4] w-full -scale-x-100 object-cover" /></div>
          <Button testId="selfie-capture-button" title="Capture selfie" icon={Camera} className="mt-4 w-full" onClick={capture} />
        </>
      ) : (
        <>
          <p className="mb-6 text-sm leading-6">
            {state === "blocked"
              ? "Your browser is blocking the camera. Enable camera access in your browser’s site settings, or upload a selfie photo you take now."
              : "We use your camera only to take a live selfie that confirms you match your ID. Nothing is recorded or stored in your browser."}
          </p>
          {state === "blocked" ? null : <Button testId="camera-permission-allow-button" title="Continue" icon={Camera} className="w-full" onClick={start} />}
        </>
      )}
      <Button testId="selfie-upload-fallback-button" title="Upload a photo instead" variant="outline" icon={Upload} className="mt-2 w-full" onClick={() => fileInput.current?.click()} />
      <Button testId="camera-permission-later-button" title="Not now" variant="ghost" className="mt-2 w-full" onClick={onClose} />
    </Modal>
  );
}
